import { FlexRow } from '@/components/common/Flex';
import Form from '@/components/common/Form';
import InputField from '@/components/common/InputField';
import Modal from '@/components/common/modals/Modal';
import PlentyLogo from '@/components/common/PlentyLogo';
import { RelationshipStatusLabel } from '@/components/common/RelationshipStatusLabel';
import { BigHeader, Header, Paragraph } from '@/components/common/Typography';
import useAutofocus from '@/components/common/useAutofocus';
import AccountLockup from '@/components/financialEntities/AccountLockup';
import { IdvProgressBar } from '@/components/idv/IdvProgressBar';
import ModalManager, { useModal } from '@/components/ModalManager';
import ShareSettingsSection from '@/components/settings/ShareSettingsSection';
import { showConfetti } from '@/util/confetti';
import { logError } from '@/util/logger';
import { Box } from '@chakra-ui/react';
import { useMyAccounts } from '@common/react/accounts';
import {
    useCreatePartnershipInvite,
    useCreatePartnershipInviteFormValues,
} from '@common/react/partnerships';
import { labelByRelationshipStatus, useCurrentUser } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import { isValidEmail } from '@common/validation';
import { useMemo } from 'react';
import { Button } from '../common/Button';
import { useToasts } from '../common/Toasts';

export interface InviteModalProps {
    title?: string;
    onComplete?: VoidFunction;
    showProgressBar?: boolean;
}

export const showInviteModal = (props: InviteModalProps = {}) =>
    ModalManager.show(InviteModal, props);

const InviteModal = ModalManager.create<InviteModalProps>(
    ({ title = 'Invite your partner', onComplete, showProgressBar }) => {
        const modal = useModal();
        const myAccounts = useMyAccounts();
        const { showSuccessToast, showErrorToast } = useToasts();
        const { formValues, setFormValue } =
            useCreatePartnershipInviteFormValues();
        const { submit, isLoading, isDisabled } = useCreatePartnershipInvite({
            formValues,
            onComplete: () => {
                modal.hide();
                showSuccessToast(TOASTS.partnerInviteSuccess);
                showConfetti();
                onComplete?.();
            },
            onError: (e: unknown) => {
                logError(e);
                showErrorToast(TOASTS.unexpectedError);
            },
        });
        const autofocusRef = useAutofocus();
        const { partnerInfo } = useCurrentUser();
        const showRelationshipStatus = useMemo(
            () => !partnerInfo?.relationshipStatus,
            // only set this once, when the modal is opened initially
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );

        return (
            <Modal
                size="4xl"
                isOpen={modal.visible}
                scrollBehavior="inside"
                onClose={modal.hide}
                isCentered={false}
                isFullScreen
                renderFooter={() => (
                    <FlexRow justifyContent="flex-end" px={20} py={10}>
                        <Button
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            onClick={submit}
                            onClickEvent="Send Partnership Invite Button Clicked"
                        >
                            Send invitation
                        </Button>
                    </FlexRow>
                )}
            >
                <Box px={10}>
                    <FlexRow mb={10} justify="space-between">
                        <PlentyLogo />
                        {showProgressBar && (
                            <IdvProgressBar
                                includeInviteStep
                                forceStep="invite-partner"
                            />
                        )}
                    </FlexRow>
                    <BigHeader mb={5}>{title}</BigHeader>
                    <Paragraph color="textMuted" mb={8}>
                        Plenty’s best with a partner. Effortlessly stay on the
                        same page as you invest and plan for your future,
                        together. Keep life organized and enjoy the flexibility
                        to share as many accounts as you’d like and keep others
                        private.
                    </Paragraph>

                    <Form disabled={false} onSubmit={submit} mb={3}>
                        <FlexRow gap={12} mb={15}>
                            <InputField
                                name="firstName"
                                label="Their first name"
                                placeholder="Their first name"
                                type="text"
                                value={formValues.firstName}
                                onChange={(value) =>
                                    setFormValue({
                                        ...formValues,
                                        firstName: value,
                                    })
                                }
                                inputOptions={{ toUpperFirst: true }}
                                ref={autofocusRef}
                            />

                            <InputField
                                name="lastName"
                                label="Their last name"
                                placeholder="Their last name"
                                type="text"
                                value={formValues.lastName}
                                onChange={(value) =>
                                    setFormValue({
                                        ...formValues,
                                        lastName: value,
                                    })
                                }
                                inputOptions={{ toUpperFirst: true }}
                            />
                        </FlexRow>

                        <FlexRow gap={12} mb={15}>
                            <FlexRow basis={0} grow={1}>
                                <InputField
                                    name="email"
                                    label="Their email"
                                    type="email"
                                    validations={[
                                        {
                                            validate: isValidEmail,
                                            error: 'Please enter a valid email',
                                        },
                                    ]}
                                    validateOnBlur
                                    placeholder="Enter their personal email"
                                    value={formValues.email}
                                    onChange={(value) =>
                                        setFormValue({
                                            ...formValues,
                                            email: value,
                                        })
                                    }
                                />
                            </FlexRow>

                            <FlexRow basis={0} grow={1}>
                                {showRelationshipStatus && (
                                    <InputField
                                        name="relationshipStatus"
                                        label={<RelationshipStatusLabel />}
                                        type="select"
                                        placeholder="Your relationship status"
                                        options={Object.entries(
                                            labelByRelationshipStatus
                                        ).map(([value, label]) => ({
                                            value,
                                            label,
                                        }))}
                                        value={formValues.relationshipStatus}
                                        onChange={(value) =>
                                            setFormValue({
                                                ...formValues,
                                                relationshipStatus: value,
                                            })
                                        }
                                    />
                                )}
                            </FlexRow>
                        </FlexRow>

                        {myAccounts && myAccounts.length > 0 && (
                            <Box my={10}>
                                <Header mb={3}>
                                    Sharing accounts with my partner
                                </Header>
                                <Paragraph color="textMuted" mb={5}>
                                    When you make your account private, your
                                    partner will not be able to see any record
                                    of it. When you make it shared, we’ll track
                                    that the account belongs to you and make it
                                    visible to your partner.
                                </Paragraph>
                                {myAccounts.map((account) => (
                                    <ShareSettingsSection
                                        key={account.id}
                                        el={
                                            <AccountLockup
                                                account={account}
                                                showBalance={true}
                                                showLastUpdated={false}
                                            />
                                        }
                                        shareStatus={
                                            formValues
                                                .accountSharingPreferences[
                                                account.id
                                            ] ?? account.shareStatus
                                        }
                                        onChange={(shareStatus) => {
                                            setFormValue({
                                                ...formValues,
                                                accountSharingPreferences: {
                                                    ...formValues.accountSharingPreferences,
                                                    [account.id]: shareStatus,
                                                },
                                            });
                                        }}
                                        // Disable if partner is joint owner
                                        isDisabled={
                                            !!account.jointOwnerPartnershipId
                                        }
                                    />
                                ))}
                            </Box>
                        )}
                    </Form>
                </Box>
            </Modal>
        );
    }
);

export default InviteModal;
